import { useCallback, useState } from 'react';

import { ListItemButton, Typography } from '@mui/material';

import {
  useHandleRowClick,
  useHandleDownloadUserFile,
} from '@components/FileBrowser/Components/hooks';
import {
  useDeleteUserFile,
  useGetUserFiles,
} from '@api/fileHandling/fileHandling';
import { useFileBrowserLocation } from '@components/FileBrowser/hooks';
import {
  FileBrowserBackButton,
  FileNameAndIconContainer,
  List,
} from '@components/FileBrowser/StyledComponents';
import NoFiles from '@components/FileBrowser/Components/NoFiles';
import { FileBrowserRow } from '@components/FileBrowser/Components/FileBrowserRow';
import DeleteConfirmation from '@/components/Dialog/DeleteConfirmation';
// import DeleteConfirmation from '@/components/Dialog/DeleteConfirmation';

const DUMMY_FILE_NAME = '.directory';

interface IFileBrowserFileListProps {
  onSelectFiles?: (files: string[]) => void;
  selectMultiple?: boolean;
  onClose: () => void;
}

export const FileBrowserFileList = ({
  onSelectFiles,
  selectMultiple,
  onClose,
}: IFileBrowserFileListProps) => {
  const { setLocation, location } = useFileBrowserLocation();
  const { data } = useGetUserFiles({ location });
  const handleDownload = useHandleDownloadUserFile();
  const { selectedFiles, handleClickRow } = useHandleRowClick({
    onSelectFiles,
    selectMultiple,
    onClose,
  });

  const handleClickBack = useCallback(() => {
    const locationLength = location.length;
    const parentLevel = location
      .substring(0, locationLength - 1)
      .lastIndexOf('/');

    setLocation(location.slice(0, parentLevel + 1));
  }, [setLocation, location]);

  const { mutate: deleteFile, isPending: isDeleting } = useDeleteUserFile();

  // store which file is about to be deleted
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string | null;
    name: string;
  } | null>(null);

  const openDelete = useCallback((id: string | null, name: string) => {
    setDeleteTarget({ id, name });
  }, []);

  const closeDelete = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!deleteTarget) return;

    deleteFile(
      { id: deleteTarget.id },
      {
        onSuccess: () => {
          closeDelete();
        },
      },
    );
  }, [deleteTarget, deleteFile, closeDelete]);

  return (
    <>
      {data.length === 0 && <NoFiles />}
      {data.length > 0 && (
        <List dense disablePadding>
          {location !== '/' && (
            <ListItemButton
              sx={{ px: { xs: 1.5, md: 2 }, py: 1.25 }}
              key="back"
              disableGutters
              onClick={() => handleClickBack()}
            >
              <FileNameAndIconContainer>
                <FileBrowserBackButton />
                <Typography variant="body2" color="text.secondary">
                  Parent directory
                </Typography>
              </FileNameAndIconContainer>
            </ListItemButton>
          )}
          {data
            .filter(
              ({ user_file_url }) => !user_file_url.includes(DUMMY_FILE_NAME),
            )
            .map(
              ({ user_file_url, created_at, is_directory, file_size, id }) => (
                <FileBrowserRow
                  key={id || user_file_url}
                  handleDelete={() => openDelete(id, user_file_url)}
                  userFileUrl={user_file_url}
                  createdAt={created_at}
                  fileSize={file_size}
                  isDirectory={is_directory}
                  handleClick={handleClickRow}
                  handleDownload={() => !is_directory && handleDownload(id)}
                  selected={
                    !is_directory &&
                    selectedFiles.has(`${location}${user_file_url}`)
                  }
                />
              ),
            )}
        </List>
      )}
      <DeleteConfirmation
        open={Boolean(deleteTarget)}
        itemKey={deleteTarget?.name}
        confirmText="delete"
        onClose={closeDelete}
        onDelete={confirmDelete}
        hideArchive
        isDeleting={isDeleting}
      />
    </>
  );
};
